import type {ConsolidatedReviewState} from '@github-ui/mergebox/sections/ReviewerSection'
import {MiniMergeStatusItem} from './MiniMergeStatusItem'
import type {MiniStatusIconProps} from './MiniStatusIcon'
import {useGoToMergeBox} from '../../../mergebox/use-go-to-mergebox'
import type {MergeabilityData} from '../use-mergeability-data'

export type ApprovalsMiniStatusProps = {
  reviewerSection: Pick<
    MergeabilityData['reviewerSection'],
    | 'numReviewsRequired' // the number of approvals required to merge the pull request from rules
    | 'codeownersRequired'
    | 'latestOpinionatedReviews'
    | 'pendingRequestedReviews'
    | 'sectionStatus'
  >
}

export function ApprovalsMiniStatus({reviewerSection}: ApprovalsMiniStatusProps) {
  const goToMergeBox = useGoToMergeBox()

  const {successful, total} = getApprovalsRatio(reviewerSection)
  const iconType = iconTypeByReviewsState[reviewerSection.sectionStatus]

  return (
    <MiniMergeStatusItem aria-label="Approvals">
      <MiniMergeStatusItem.LabelRow>
        <MiniMergeStatusItem.Label icon={iconType} successful={successful} total={total}>
          Approvals
        </MiniMergeStatusItem.Label>
        <MiniMergeStatusItem.Action
          aria-label="View approvals"
          // TODO: Update the panel to open the approvals section in the merge status panel. The current open panel options
          // don't support expanding sections within the merge status panel
          onClick={event => goToMergeBox(event.currentTarget)}
        />
      </MiniMergeStatusItem.LabelRow>
      <MiniMergeStatusItem.Progress
        aria-label={`${successful} of ${total} approvals`}
        successful={successful}
        total={total}
      />
    </MiniMergeStatusItem>
  )
}

function getApprovalsRatio({
  numReviewsRequired,
  codeownersRequired,
  latestOpinionatedReviews,
  pendingRequestedReviews,
}: ApprovalsMiniStatusProps['reviewerSection']) {
  const approvedReviews = latestOpinionatedReviews.filter(review => review.state === 'APPROVED')
  const approvedReviewCount = approvedReviews.filter(review => review.authorCanPushToRepository).length

  if (codeownersRequired) {
    // Manually build codeowner approval ratio because the rule doesn't expose the number of required codeowners - only the fulfillment.
    // The rule doesn't expose how many have been fulfilled or how many are pending, so we have to count the pending codeowners
    // and add them to the approved count to get a total. This can inflate the total count if an approval is received
    // from a non-codeowner, but this is the best we can do with the data we have.
    const pendingCodeownerCount = pendingRequestedReviews.filter(request => request.isCodeOwner).length
    return {
      successful: approvedReviewCount,
      // Take the most restrive count between the two rules so that we don't undercount the total required approvals.
      total: Math.max(numReviewsRequired, approvedReviewCount + pendingCodeownerCount),
    }
  }

  return {successful: approvedReviewCount, total: numReviewsRequired}
}

const iconTypeByReviewsState: Record<ConsolidatedReviewState, MiniStatusIconProps['type']> = {
  APPROVED: 'success',
  CHANGES_REQUESTED: 'failure',
  REVIEW_REQUIRED: 'warning',
  REVIEWED: 'success',
  REVIEW_REQUESTED: 'warning',
}
